import { FC, useState } from "react"
import { useParams } from "react-router-dom"
import { useApp } from "../AppProvider"
import { ErrorMessage } from "../components/common/Feedback/ErrorMessage"
import { LoadingMessage } from "../components/common/Feedback/LoadingMessage"
import Modal from "../components/common/Modal"
import { ShareModal } from "../components/common/ShareModal"
import { EmailEditor } from "../components/emails/EmailEditor"
import { PatientDetailsHeader } from "../components/patients/PatientDetailsHeader"
import { PatientLabReportsTable } from "../components/patients/PatientLabReportsTable"
import { PatientLabValuesTable } from "../components/patients/PatientLabValuesTable"
import { PatientOverview } from "../components/patients/PatientOverview"
import { Recommendations } from "../components/results/Recommendations"
import { useGetPatient } from "../hooks/usePatients"

export const PatientDetailsPage: FC = () => {
  const { token, orgId, isAnonymous, shareData, anonToken } = useApp()
  const params = useParams()

  // In anonymous mode, use the resource ID from share data
  // Otherwise, use the patientId from URL params
  const patientId =
    isAnonymous && shareData?.resourceType === "patient" ? shareData.resourceId : (params?.patientId ?? "")

  // In anonymous mode, use the share token instead of JWT token
  const effectiveToken = isAnonymous ? anonToken : token

  const query = useGetPatient(orgId, patientId, effectiveToken ?? "")
  const patient = query?.data

  const [isEmailEditorOpen, setIsEmailEditorOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  if (query.isLoading) {
    return <LoadingMessage />
  }

  if (query.isError || !patient) {
    return <ErrorMessage message={query?.error?.message ?? "Unknown application error"} />
  }

  return (
    <div className="flex flex-col gap-y-5">
      <PatientDetailsHeader
        patient={patient}
        setIsEmailEditorOpen={setIsEmailEditorOpen}
        setIsShareModalOpen={setIsShareModalOpen}
        isAnonymous={isAnonymous}
      />
      <div className="grid grid-cols-1 gap-x-3 gap-y-4 md:grid-cols-12">
        <div className="card col-span-1 md:col-span-4 lg:col-span-4">
          <PatientOverview patient={patient} />
        </div>
        <div className="card col-span-1 max-h-[400px] overflow-y-auto md:col-span-8 lg:col-span-8">
          <Recommendations recommendations={patient.recommendations} />
        </div>
        <div className="3xl:col-span-6 col-span-1 md:col-span-full lg:col-span-7">
          <PatientLabValuesTable labValues={patient.labValues} />
        </div>
        <div className="3xl:col-span-6 col-span-1 md:col-span-full lg:col-span-5">
          <PatientLabReportsTable labReports={patient?.labReports} isAnonymous={isAnonymous} />
        </div>
      </div>
      {!isAnonymous && (
        <>
          <Modal open={isEmailEditorOpen} setOpen={() => setIsEmailEditorOpen(false)}>
            <EmailEditor patient={patient} submitCb={() => setIsEmailEditorOpen(false)} />
          </Modal>
          <ShareModal
            open={isShareModalOpen}
            setOpen={setIsShareModalOpen}
            resourceType="patient"
            resourceId={patient._id}
            title="Share Patient"
          />
        </>
      )}
    </div>
  )
}
